import React from 'react';
import shortid from 'shortid';

import { computed, observable, action, makeObservable } from 'mobx';

import { omit, pick, areStringsInsensitivelyEqual, getOrCreate, capitalizeString } from 'shared/utils/utils'
import { Type, isAdminToolFlowstep } from 'shared/utils/flowstep';
import * as QA from 'shared/utils/queryAnalysis';

import * as Validation from './validation';


export class Manager {
  @observable showLabels = true;

  @observable flowstepType = Type.Insert;
  
  @observable filteredObjectFields = [];
  @observable objectFieldsToIgnore = new Set();
  @observable excelHeaders = [];
  
  @observable mapping = {
    objectName: '',
    columnNameForResultIds: '',
    externalIdFieldName: '',
    loadEmptyValues: false,
    mappedHeaders: [],
    constants: [],
    ownerChangeOptions: [],
  };

  @observable validationResult = new Validation.ValidationResult();
  
  originalHeadersCount = 0;
  @observable isExactMapping = false;
  @observable isConfirmedMapping = false;
  
  usedFieldsStack = []; // For forget/reset functionality
  excelHeadersToIgnore = new Set();
  lookupFieldsDefaultOptions = new Map();

  static generateId() {
    return shortid.generate();
  }

  static generateConstant() {
    return { 
      id: Manager.generateId(), 
      type: Validation.Source.Constant,
      value: '',
      fieldData: null,
    };
  }

  constructor({ salesforceStore }, { onSetDirty, objectFieldsToIgnore = [] }) {
    makeObservable(this);

    this.setDirty = onSetDirty;
    this.salesforceStore = salesforceStore;
    this.objectFieldsToIgnore = new Set(objectFieldsToIgnore);
    this.usedFieldsStack.push(new Validation.UsageMap());
  }

  @computed get excelHeaderOptions() {
    return this.excelHeaders.map(x => x.name);
  }

  @computed get objectFieldStrictOptions() {
    const selector = this.showLabels ? (i) => i.label : (i) => i.name;
    return this.filteredObjectFields.reduce((out, fieldData, index) => {
      const { field } = fieldData;
      const { readOnly, type, name: id } = field;
      if (!this.objectFieldsToIgnore.has(QA.toCanonicalForm(id))) {
        out.push({ key: id, id, text: selector(field), type, readOnly, fieldData });
      }
      return out;
    }, []);
  }

  @computed get objectFieldOptions() {
    return [{ key: null, text: 'Not selected' }, ...this.objectFieldStrictOptions];
  }

  @computed get status() {
    const { mappedHeaders: m, constants: c } = this.mapping;
    const { mapping, constants, general, hasAnyError } = this.validationResult;
    return {
      hasAnyError,
      generalErrors: general,
      mapping: {
        usedCount: m.reduce((out, x) => out + (x.isUsed ? 1 : 0), 0),
        totalCount: m.length,
        errors: mapping,
      },
      constants: {
        totalCount: c.length, 
        errors: constants
      },
    };
  };

  @computed get isValid() {
    return !this.status.hasAnyError;
  }

  @computed get showOwnerChangeSettings() { return this.isUpdate || this.isUpsert; }

  @computed get isUpsert() { return this.flowstepType === Type.Upsert; }
  @computed get isUpdate() { return this.flowstepType === Type.Update; }
  @computed get isInsert() { return this.flowstepType === Type.Insert; }

  get usedFields() { return this.usedFieldsStack[this.usedFieldsStack.length - 1]; }

  isMandatoryField(name) { return this.isUpdate && QA.KnownFields.Id === QA.toCanonicalForm(name); }

  @action reset(source) {
    const rm = source & Validation.Source.Mapping;
    const rc = source & Validation.Source.Constant;

    this.mapping = {
      ...this.mapping,
      mappedHeaders: rm ? [] : this.mappedHeader,
      externalIdFieldName: '',
      constants: rc ? [] : this.mapping.constants,
    };

    rm && (this.originalHeadersCount = 0);
    this.reloadChanges(source);

    this.isConfirmedMapping = false;
    this.isExactMapping = false;
    this.validateAndMarkDirty();
  }

  @action setType(type) {
    if (this.flowstepType !== type) {
      this.flowstepType = type;
      
      // this.isExactMapping remains intact, so we need to load in this.loadMapping instead of 
      // guessing it or merging. But a confirmation from an user should be obtained anyway
      this.isConfirmedMapping = false;
      this.validateAndMarkDirty();
    }
  }

  @action setObjectName(objectName) {
    if (this.mapping.objectName !== objectName) {
      this.mapping.objectName = objectName;
      this.reset(Validation.Source.All);
    }
  }

  @action setColumnNameForResultIds(value) {
    this.mapping.columnNameForResultIds = value;
    this.setDirty();
  }

  @action setLoadEmptyValues(value) {
    this.mapping.loadEmptyValues = value;
    this.setDirty();
  }

  @action setExcelHeaders(excelHeaders) {
    this.excelHeaders = excelHeaders.map((name, index) => ({ 
      name, index, id: Manager.generateId()
    }));
    this.reset(Validation.Source.Mapping);
  }

  @action setExcelHeadersToIgnore(indexes) {
    this.excelHeadersToIgnore = new Set(indexes);
    this.isConfirmedMapping = false;
    this.validateAndMarkDirty();
  }

  @action setMapping(mapping, { isExact = true }) {

    const { 
      mappedHeaders: mhParam, constants: cParam = [], externalIdFieldName, ownerChangeOptions = [],
      loadEmptyValues, columnNameForResultIds, sfObjectName: objectName 
    } = mapping;

    // Convert parameterization formats of mappings and constants
    // to the ones suitable for editors and validators
    const constants = cParam.map(constant => {
      const id = Manager.generateId();
      const { field, value, objectName } = constant; 
      const { name } = field;
      return { id, value, fieldData: { name, objectName, field }, type: Validation.Source.Constant };
    });

    const mappedHeaders = this.excelHeaders.map(excelHeader => {
      const { id, index } = excelHeader;
      const mappedHeader = index < mhParam.length ? mhParam[index] : null;

      // Create fake fieldData: the actual information will be retrived in this.loadMappin() when an user opens Mapping Manager
      // But if for some reason he doens't do that any simply presses 'save' button we should still generate a valid
      // mapping in this.makeMapping()
      const fieldData = mappedHeader?.sfField 
        ? { field: mappedHeader.sfField, name: mappedHeader.sfField.name } 
        : null;
      const isUsed = !!mappedHeader?.isUsed;
      return { id, isUsed, excelHeader, fieldData, lookupObject: mappedHeader?.lookupObject, type: Validation.Source.Mapping };
    });

    this.originalHeadersCount = mhParam.length;

    // if (isExact) {
    //   this.isConfirmedMapping = true;
    // }

    // Consider mapping with different header length as not exact
    // this.isExactMapping = isExact && this.originalHeadersCount === this.excelHeaders.length;
    this.isExactMapping = isExact;
    this.isConfirmedMapping = this.isExactMapping;

    this.mapping = {
      mappedHeaders, constants, externalIdFieldName, ownerChangeOptions,
      objectName, loadEmptyValues, columnNameForResultIds,
    };

    // Don't add fields from not exact mapping to the used fields. We will add them in this.loadMapping()
    // this.reloadChanges(Validation.Source.Constant | (this.isExactMapping ? Validation.Source.Mapping : 0));
    this.reloadChanges(Validation.Source.All);
    this.validationResult = this.validate();
  }


  makeMapping() {
    const { 
      mappedHeaders: m, constants: c, externalIdFieldName, objectName, loadEmptyValues, columnNameForResultIds, ownerChangeOptions
    } = this.mapping;

    const constants = c.map(({ value, fieldData: { objectName, field } }) => {
      return { value, objectName, field };
    });

    const mappedHeaders = m.map(({ excelHeader, isUsed, fieldData, lookupObject}) => {
      return { excelColumnName: excelHeader.name, isUsed, sfField: fieldData?.field, lookupObject};
    });

    return {
      loadEmptyValues, columnNameForResultIds, externalIdFieldName,
      sfObjectName: objectName,
      mappedHeaders, constants, ownerChangeOptions,
    };
  }

  //
  //

  addToUsedFields(item) {
    const { id, isUsed, fieldData, excelHeader, type } = item; 
    const name = fieldData?.name;
    type === Validation.Source.Constant
      ? fieldData && this.usedFields.add({ id, name, fieldData, type })
      : isUsed && fieldData && this.usedFields.add({ id, name, fieldData, type, excelHeader });
  }

  addAllToUsedFields(items) {
    items.forEach(item => this.addToUsedFields(item));
  }

  removeFromUsedFields(item) {
    const { id, fieldData } = item;
    fieldData && this.usedFields.remove({ id, name: fieldData.name });
  }

  reloadChanges(source) {
    const c = source & Validation.Source.Constant;
    const m = source & Validation.Source.Mapping;

    if (c) {
      this.usedFields.removeAllFromSource(c);
      this.addAllToUsedFields(this.mapping.constants);
    }

    if (m) {
      this.usedFields.removeAllFromSource(m);
      this.isExactMapping && this.addAllToUsedFields(this.mapping.mappedHeaders);
    }
  }

  rememberChanges() {
    this.usedFieldsStack.push(this.usedFields.clone());
  }

  forgetChanges() {
    this.usedFieldsStack.pop();
  }

  saveChanges() {
    const changes = this.usedFields;
    this.usedFieldsStack.pop();
    this.usedFieldsStack[this.usedFieldsStack.length - 1] = changes;
  }

  //
  loadConstants() {
    this.rememberChanges();

    return this.mapping.constants.map(data => {
      const { fieldData, id, value } = data;
      const { name } = fieldData;
      const resolvedFieldData = this.filteredObjectFields.find(x => x.field.name === name);
      return { id, value, fieldData: resolvedFieldData };
    });
  }

  //

  //
  //
  loadMapping(generateIfInvalid) {
    this.rememberChanges();
    const { isExactMapping, mapping, excelHeaders } = this;

    const isInvalid = this.originalHeadersCount !== this.excelHeaders.length;
    const forceGenerate = isInvalid && generateIfInvalid;
    const simplyLoad = isExactMapping && !forceGenerate;
    
    const res = simplyLoad
      ? this.loadExactMapping(mapping)
      : !mapping.mappedHeaders
        ? this.generateMapping(excelHeaders)
        : this.mergeMappings(mapping, excelHeaders);

    // If we load exact mapping, then usedFields will contain all fields from mapping. See this.setMapping()
    // However when we generate mapping (either by guessing or merging) we need to populate
    // used fields here
    if (!simplyLoad) {
      this.usedFields.removeAllFromSource(Validation.Source.Mapping);
      this.addAllToUsedFields(res.mappedHeaders);
      this.setDirty();
    }

    return { ...res, isDirty: !simplyLoad };
  }

  loadExactMapping(mapping) {
    const { mappedHeaders: m, externalIdFieldName } = mapping;
    const mappedHeaders = m.map(header => {
      const { fieldData } = header;
      const resolvedFieldData = fieldData ? this.filteredObjectFields.find(x => x.name === fieldData.name) : null;
      return { ...header, fieldData: resolvedFieldData }; 
    });

    return { mappedHeaders, externalIdFieldName };
  }

  mergeMappings(existingMapping, excelHeaders) {
    const { mappedHeaders, externalIdFieldName } = existingMapping;
    
    const generatedMapping = this.generateMapping(excelHeaders);
    const mappedHeadersUpper = mappedHeaders.map(x => ({ ...x, excelHeaderNameUpper: x.excelHeader.name.toUpperCase() }));

    const mergedMappedHeaders = generatedMapping.mappedHeaders.map(generatedHeader => {
      const index = mappedHeadersUpper.findIndex(y => y.excelHeaderNameUpper === generatedHeader.excelHeader.name.toUpperCase());
      if (index === -1 ) { return generatedHeader; }

      const { isUsed, fieldData } = mappedHeadersUpper.splice(index, 1)[0];  // Exclude from search
      const newFieldData = fieldData ? this.filteredObjectFields.find(x => x.name === fieldData.name) : null;
      const newIsUsed = isUsed || (newFieldData && this.isMandatoryField(newFieldData.name));

      return { ...generatedHeader, isUsed: newIsUsed, fieldData: newFieldData };
    });

    const mergedExternalIdFieldName = externalIdFieldName
      ? mergedMappedHeaders.find(x => x.fieldData?.name === externalIdFieldName) // Yes, it extists, but might be disabled - it's ok
        ? externalIdFieldName
        : mergedMappedHeaders.length ? (mergedMappedHeaders[0].fieldData?.name ?? '') : ''
      : generatedMapping.externalIdFieldName;

    return { mappedHeaders: mergedMappedHeaders, externalIdFieldName: mergedExternalIdFieldName ?? '' };
  }

  generateMapping(excelHeaders) {
    const fields = this.filteredObjectFields.map(fieldData => {
      const { name, label } = fieldData.field;
      return { labelUpper: label.toUpperCase(), nameUpper: name.toUpperCase(), fieldData };
    });

    const doesFullMatch = (headerUpper, { labelUpper, nameUpper }) => headerUpper && (nameUpper === headerUpper || labelUpper === headerUpper);
    const doesPartialMatch = (headerUpper, { labelUpper, nameUpper }) => headerUpper &&
      (headerUpper.includes(nameUpper) || headerUpper.includes(labelUpper) || nameUpper.includes(headerUpper) || labelUpper.includes(headerUpper));

    const tryMatch = (mappedHeader, matcher) => {
      const { excelHeader, fieldData: initialFieldData, id, type } = mappedHeader;

      if (!initialFieldData) {
        const index = fields.findIndex(field => matcher(excelHeader.name.toUpperCase(), field));
        if (index !== -1) {
          const { fieldData } = fields.splice(index, 1)[0]; // Exclude this entry from search
          const { field } = fieldData;
          const isUsed = !field.readOnly || this.isMandatoryField(field.name); // Update must have Id mapped to some column
          return { id, excelHeader, isUsed, fieldData, type };
        }
      }

      return mappedHeader;
    };

    const mappedHeaders = excelHeaders
      .map(excelHeader => ({ excelHeader, isUsed: false, fieldData: null, id: excelHeader.id, type: Validation.Source.Mapping }))
      .map(x => tryMatch(x, doesFullMatch))
      .map(x => tryMatch(x, doesPartialMatch));

    const externalIdFieldName = mappedHeaders.find(x => x.isUsed && x.fieldData)?.name ?? '';
    return { mappedHeaders, externalIdFieldName };
  }
  //
  //


  async loadObjectFields() {
    const allObjectFields = await this.getObjectFields(this.mapping.objectName);
    this.filteredObjectFields = allObjectFields
      .filter(x => !this.objectFieldsToIgnore.has(QA.toCanonicalForm(x.field.name)));
    return this.filteredObjectFields;
  }

  async getObjectFields(objectName) {
    if (!objectName) { return []; }

    const rawFields = await this.salesforceStore.fetchObjectFields(objectName);
    const allFields = rawFields
      .map(rawField => ({ name: rawField.name, rawField, field: rawField, objectName, isLookup: QA.isLookupField(rawField)}));

    if (this.isUpsert) {
      const refFields = await this.getRelationshipFields(rawFields);
      allFields.push(...refFields);
    }

    return allFields.map(x => ({ 
      ...x,
      field: { readOnly: !x.rawField.updateable, ...pick(x.field, ['label', 'name', 'type', 'idLookup', 'upsertRelationship']) }, 
    }));
  }

  async getRelationshipFields(allRawFields) {
    const refFields = [];
    const referencedFields = allRawFields.filter(QA.isRelationshipField);

    for (const rawField of referencedFields) {
      const references = QA.getRelationshipReferences(rawField);
      for (const refObjectName of references) {
        const refRawFields = await this.salesforceStore.fetchObjectFields(refObjectName);
        const refObjectFields = refRawFields
          .filter(x => !!x.externalId)
          .map(refRawField => {
            const name = `${rawField.name}/${refObjectName}/${refRawField.name}`;
            const label = `${rawField.label}/${refObjectName}/${refRawField.label}`;
            const upsertRelationship = { fieldName: name, relationshipName: rawField.relationshipName, refObjectName, refFieldName: refRawField.name };

            // console.log(`Field '${rawField.name}' for ref object '${refObjectName}' and ref field '${refRawField.name}': `, refRawField, name, label, upsertRelationship);
           
            return { name, field: {...refRawField, name, label, upsertRelationship }, rawField: refRawField, objectName: refObjectName, isLookup : QA.isLookupField(refRawField) };
          });

        refFields.push(...refObjectFields);
      }
    }

    return refFields;
  }

  
  static getLookupFieldKey(fieldData) {
    return `${capitalizeString(fieldData.objectName)}/${capitalizeString(fieldData.name)}`;
  }

  getDefaultLookupOption(fieldData) {
    return this.lookupFieldsDefaultOptions.get(Manager.getLookupFieldKey(fieldData));
  }

  async getLookupOptions(fieldData) {
    const options = [];
    const defaultOptionKey = Manager.getLookupFieldKey(fieldData);
    let defaultOption = this.lookupFieldsDefaultOptions.get(defaultOptionKey);
    const references = QA.isRelationshipField(fieldData.rawField) ? QA.getRelationshipReferences(fieldData.rawField) : [fieldData.objectName];
    await Promise.all(references.map(async (refObjectName) => {
      const refRawFields = await this.salesforceStore.fetchObjectFields(refObjectName);
      const defaultOptionName = QA.getNameFieldName(refObjectName);
      const refObjectFields = refRawFields
        .filter(field => [QA.FieldTypeName.String, QA.FieldTypeName.Phone, QA.FieldTypeName.Email].includes(field.type)
          //event doesn't have and unique fields exept combobox subject
          || (areStringsInsensitivelyEqual(refObjectName, QA.KnownObjects.Event) && areStringsInsensitivelyEqual(field.name, QA.KnownFields.Subject)))
        .map(refRawField => {
          const name = `${refObjectName}/${refRawField.name}`;
          const label = `${refObjectName}/${refRawField.label}`;
          const refFieldData = { name, label, rawField: refRawField, field: refRawField, objectName: refObjectName };
          refFieldData.field = {
            readOnly: !refRawField.updateable, name, label, ...pick(refRawField, ['type', 'idLookup'])
          }
          const { readOnly, type, name: id } = refFieldData.field;

          if ((!defaultOption || refObjectName.localeCompare(defaultOption.objectName) === -1)
            && areStringsInsensitivelyEqual(refRawField.name, defaultOptionName)) {
            defaultOption = {
              name: refRawField.name,
              objectName: refObjectName
            };
          }
          return { key: id, id, text: this.showLabels ? label : name, type, readOnly, fieldData: refFieldData };
        });
      options.push(...refObjectFields);
    }));
    if (defaultOption) {
     this.lookupFieldsDefaultOptions.set(defaultOptionKey, defaultOption); 
    }
    return options;
  }

  ///
  validateAndMarkDirty() {
    this.validationResult = this.validate();
    this.setDirty();
  }

  saveChangesAndValidate() {
    this.saveChanges();
    this.validateAndMarkDirty();
  }

  validate(overrideCtx = {}, toValidate = Validation.Source.All) {
    const ctx = this.makeValidationContext(overrideCtx);
    return Validation.ValidationResult.fromCtx(ctx, toValidate);
  }

  makeValidationContext(overrideCtx = {}) {
    const { mappedHeaders: mhOver, constants: cOver } = overrideCtx;

    // If we are supplied with either mappedHeaders or constants, then simply check this mapping as confirmed
    const isConfirmedMapping = !!mhOver || !!cOver || this.isConfirmedMapping;
    // Mapping coming from editor always has corrent length
    const originalHeadersCount = mhOver ? mhOver.length : this.originalHeadersCount;
    const { mapping, flowstepType, usedFields, excelHeadersToIgnore, excelHeaders } = this;

    return { 
      flowstepType, usedFields, isConfirmedMapping, originalHeadersCount, excelHeadersToIgnore, excelHeaders,
      mapping: {
        mappedHeaders: [...mhOver ?? mapping.mappedHeaders],
        constants: [...cOver ?? mapping.constants],
        externalIdFieldName: overrideCtx.externalIdFieldName ?? mapping.externalIdFieldName,
    }};
  }

  ///
  saveMappedHeaders(mapping) {
    this.mapping = {
      ...this.mapping,
      ...mapping,
    };

    this.originalHeadersCount = mapping.mappedHeaders.length;
    this.isExactMapping = true;
    this.isConfirmedMapping = true;
    this.saveChangesAndValidate();
  }

  saveConstants(constants) {
    this.mapping.constants = constants;
    this.saveChangesAndValidate();
  }

  saveOwnerChangeOptions(options) {
    this.mapping.ownerChangeOptions = options;
    this.setDirty();
  }
};


export default Manager;